import React, { Dispatch, SetStateAction } from 'react'

import {
    BoldText,
    StyledFinanceModalContent,
    FinanceModalParagraph,
    FinanceModalSubtitle,
    FinanceModalTitle,
    LegalTextLink,
    LegalTextParagraph,
    StyledAccordionRow,
    StyledInfoModal,
    LegalTitle,
} from './search-listing-details.styles'

import { BulletList, BulletListType, ModalVariant } from '@at/design-system-patterns'
import { PortalComponent } from '../modals/portal'
import { FinanceRepresentativeExample } from './finance-representative-example'
import { FinanceLegalText } from './finance-representative-example.styles'
import {
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph_components,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph_components_FinanceListingDisclaimerLinkComponent,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph_components_FinanceListingDisclaimerTitleComponent,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_representativeValues_RepresentativeValues,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { FinanceListingProvider, QuoteSubType } from '../../__generated__/fed-graphql-types'
import { focusOnElement } from '../../utils/focus-on-element'
import {
    FINANCE_LEARN_MORE_0_PCP_BODY_LEARN_MORE_PCP_STEPS_BODY_0_STATIC_COPY,
    FINANCE_LEARN_MORE_0_PCP_BODY_LEARN_MORE_PCP_STEPS_BODY_1_STATIC_COPY,
    FINANCE_LEARN_MORE_0_PCP_BODY_LEARN_MORE_PCP_STEPS_BODY_2_STATIC_COPY,
    FINANCE_LEARN_MORE_0_PCP_BODY_PCP_MILEAGE_LIMIT_BODY_STATIC_COPY,
    FINANCE_LEARN_MORE_0_PCP_BODY_WHAT_IS_PCP_BODY_STATIC_COPY,
    FINANCE_LEARN_MORE_0_PCP_TITLE_STATIC_COPY,
    FINANCE_LEARN_MORE_2_HP_BODY_0_STATIC_COPY,
    FINANCE_LEARN_MORE_2_HP_BODY_1_STATIC_COPY,
    FINANCE_LEARN_MORE_2_HP_BODY_2_STATIC_COPY,
    FINANCE_LEARN_MORE_2_HP_BODY_3_STATIC_COPY,
    FINANCE_LEARN_MORE_2_HP_TITLE_STATIC_COPY,
    FINANCE_LEARN_MORE_3_CS_BODY_0_STATIC_COPY,
    FINANCE_LEARN_MORE_3_CS_BODY_1_STATIC_COPY,
    FINANCE_LEARN_MORE_3_CS_BODY_2_STATIC_COPY,
    FINANCE_LEARN_MORE_3_CS_BODY_3_STATIC_COPY,
    FINANCE_LEARN_MORE_3_CS_TITLE_STATIC_COPY,
} from '@at/consumer-ui-text/lib/finance//shared/finance.shared.learn-more'
import { IconInformation } from '@at/design-system-icons'

// type guard to differentiate between the two types of components
const isLinkComponent = (
    component: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph_components
): component is SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph_components_FinanceListingDisclaimerLinkComponent => {
    return component.__typename === 'FinanceListingDisclaimerLinkComponent'
}

const isTitleComponent = (
    component: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph_components
): component is SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph_components_FinanceListingDisclaimerTitleComponent => {
    return component.__typename === 'FinanceListingDisclaimerTitleComponent'
}

export const FinanceModalContent = ({
    showModal,
    setShowModal,
    representativeValues,
    disclaimerText,
    financeProvider,
    financeAdvertId,
    isFlexibleFinance,
    quoteType,
}: {
    showModal: boolean
    setShowModal: Dispatch<SetStateAction<boolean>>
    representativeValues?:
        | ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_representativeValues_RepresentativeValues>
        | undefined
    disclaimerText?:
        | ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph>
        | undefined
    financeProvider?: FinanceListingProvider
    financeAdvertId: string
    isFlexibleFinance?: boolean
    quoteType?: QuoteSubType.CS | QuoteSubType.HP | QuoteSubType.PCP
}) => {
    const isPCP = representativeValues?.some((value) => value.financeValue === QuoteSubType.PCP)

    const getModalContent = () => {
        switch (quoteType) {
            case QuoteSubType.PCP:
                return {
                    title: 'Personal contract purchase (PCP) finance breakdown',
                    accordion: (
                        <StyledAccordionRow
                            rowIndex={0}
                            icon={<IconInformation title={'Information'} />}
                            rowHeading={FINANCE_LEARN_MORE_0_PCP_TITLE_STATIC_COPY}
                            rowContent={
                                <FinanceModalParagraph as="p">
                                    {FINANCE_LEARN_MORE_0_PCP_BODY_WHAT_IS_PCP_BODY_STATIC_COPY}
                                    <BulletList
                                        items={[
                                            FINANCE_LEARN_MORE_0_PCP_BODY_LEARN_MORE_PCP_STEPS_BODY_0_STATIC_COPY,
                                            FINANCE_LEARN_MORE_0_PCP_BODY_LEARN_MORE_PCP_STEPS_BODY_1_STATIC_COPY,
                                            FINANCE_LEARN_MORE_0_PCP_BODY_LEARN_MORE_PCP_STEPS_BODY_2_STATIC_COPY,
                                        ]}
                                        listType={BulletListType.STANDARD}
                                    />
                                    {FINANCE_LEARN_MORE_0_PCP_BODY_PCP_MILEAGE_LIMIT_BODY_STATIC_COPY}
                                </FinanceModalParagraph>
                            }
                        />
                    ),
                }
            case QuoteSubType.HP:
                return {
                    title: 'Hire purchase (HP) finance breakdown',
                    accordion: (
                        <StyledAccordionRow
                            rowIndex={0}
                            icon={<IconInformation title={'Information'} />}
                            rowHeading={FINANCE_LEARN_MORE_2_HP_TITLE_STATIC_COPY}
                            rowContent={
                                <>
                                    <FinanceModalParagraph as="p">
                                        {FINANCE_LEARN_MORE_2_HP_BODY_0_STATIC_COPY}
                                    </FinanceModalParagraph>
                                    <FinanceModalParagraph as="p">
                                        {FINANCE_LEARN_MORE_2_HP_BODY_1_STATIC_COPY}
                                    </FinanceModalParagraph>
                                    <FinanceModalParagraph as="p">
                                        {FINANCE_LEARN_MORE_2_HP_BODY_2_STATIC_COPY}
                                    </FinanceModalParagraph>
                                    <FinanceModalParagraph as="p">
                                        {FINANCE_LEARN_MORE_2_HP_BODY_3_STATIC_COPY}
                                    </FinanceModalParagraph>
                                </>
                            }
                        />
                    ),
                }
            case QuoteSubType.CS:
                return {
                    title: 'Conditional sale (CS) finance breakdown',
                    accordion: (
                        <StyledAccordionRow
                            rowIndex={0}
                            icon={<IconInformation title={'Information'} />}
                            rowHeading={FINANCE_LEARN_MORE_3_CS_TITLE_STATIC_COPY}
                            rowContent={
                                <>
                                    <FinanceModalParagraph as="p">
                                        {FINANCE_LEARN_MORE_3_CS_BODY_0_STATIC_COPY}
                                    </FinanceModalParagraph>
                                    <FinanceModalParagraph as="p">
                                        {FINANCE_LEARN_MORE_3_CS_BODY_1_STATIC_COPY}
                                    </FinanceModalParagraph>
                                    <FinanceModalParagraph as="p">
                                        {FINANCE_LEARN_MORE_3_CS_BODY_2_STATIC_COPY}
                                    </FinanceModalParagraph>
                                    <FinanceModalParagraph as="p">
                                        {FINANCE_LEARN_MORE_3_CS_BODY_3_STATIC_COPY}
                                    </FinanceModalParagraph>
                                </>
                            }
                        />
                    ),
                }
            default:
                return {}
        }
    }

    return (
        <>
            {showModal && (
                <PortalComponent show={showModal}>
                    <StyledInfoModal
                        isShowing={true}
                        closeText={'Back'}
                        hideFn={() => {
                            setShowModal(false)
                            focusOnElement(financeAdvertId)
                        }}
                        variation={ModalVariant.SLIDE}
                        hasBackLink={false}
                        modalTitle="Hire purchase/Conditional sale breakdown"
                        focusId="modal-button"
                        modalHashName="finance-info-modal"
                        className="info-modal"
                    >
                        <StyledFinanceModalContent>
                            {isFlexibleFinance ? (
                                <FinanceModalTitle as="h1">{getModalContent().title}</FinanceModalTitle>
                            ) : (
                                <FinanceModalTitle as="h1">
                                    {isPCP
                                        ? 'Personal contract purchase finance breakdown'
                                        : 'Hire purchase/Conditional sale breakdown'}
                                </FinanceModalTitle>
                            )}

                            <FinanceModalParagraph as="p">
                                Here&apos;s how much it could cost if you take out finance on this vehicle.
                                <br />
                                <BoldText>Please note:</BoldText>{' '}
                                {financeProvider === FinanceListingProvider.ZUTO
                                    ? 'Finance is arranged by Zuto who '
                                    : 'The seller '}
                                can give you a personalised quote which considers your personal circumstances.
                            </FinanceModalParagraph>
                            {isFlexibleFinance && getModalContent().accordion}
                            <FinanceModalSubtitle as="h2">Representative example</FinanceModalSubtitle>
                            {representativeValues?.map((value) => {
                                return (
                                    <FinanceRepresentativeExample
                                        key={value.financeKey}
                                        title={value.financeKey}
                                        value={value.financeValue}
                                    ></FinanceRepresentativeExample>
                                )
                            })}
                            <FinanceModalSubtitle as="h2">The legal bit</FinanceModalSubtitle>
                            <FinanceLegalText as="div">
                                {disclaimerText?.map(({ components }, i) => {
                                    return (
                                        <div key={i}>
                                            {components?.map((component, index) => {
                                                if (isLinkComponent(component)) {
                                                    return (
                                                        <LegalTextLink
                                                            as="a"
                                                            key={index}
                                                            href={component.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {component.text}
                                                        </LegalTextLink>
                                                    )
                                                } else if (isTitleComponent(component)) {
                                                    return (
                                                        <LegalTitle as="p" key={index}>
                                                            {component.text}
                                                        </LegalTitle>
                                                    )
                                                } else {
                                                    return (
                                                        <LegalTextParagraph as="p" key={index}>
                                                            {component.text}
                                                        </LegalTextParagraph>
                                                    )
                                                }
                                            })}
                                        </div>
                                    )
                                })}
                            </FinanceLegalText>
                        </StyledFinanceModalContent>
                    </StyledInfoModal>
                </PortalComponent>
            )}
        </>
    )
}
